import React from 'react';
import { UserBalance, CurrencyCode } from '../types/user.entity';
import { FaCoins, FaChartLine, FaShieldAlt, FaClock } from 'react-icons/fa';
import { useTheme } from "src/context/theme.context";

interface UserBalanceDisplayProps {
  balance: UserBalance | null;
  isLoading: boolean;
}

const currencyConfig = {
  EUR: {
    symbol: '💶',
    name: 'Euro',
    bgColor: 'bg-gradient-to-br from-blue-50 to-blue-100',
    textColor: 'text-blue-800',
    borderColor: 'border-blue-200',
    headerBg: 'bg-gradient-to-br from-blue-500 to-blue-600',
    progressColors: {
      available: 'bg-gradient-to-r from-green-500 to-green-600',
      reserved: 'bg-gradient-to-r from-amber-500 to-amber-600',
      holding: 'bg-gradient-to-r from-gray-500 to-gray-600'
    },
    icon: FaCoins
  },
  KARMA: {
    symbol: '✨',
    name: 'Karma',
    bgColor: 'bg-gradient-to-br from-emerald-50 to-emerald-100',
    textColor: 'text-emerald-800',
    borderColor: 'border-emerald-200',
    headerBg: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
    progressColors: {
      available: 'bg-gradient-to-r from-green-500 to-green-600',
      reserved: 'bg-gradient-to-r from-amber-500 to-amber-600',
      holding: 'bg-gradient-to-r from-gray-500 to-gray-600'
    },
    icon: FaChartLine
  }
};

export const UserBalanceDisplay: React.FC<UserBalanceDisplayProps> = ({ balance, isLoading }) => {
  const { isDarkMode } = useTheme();

  if (isLoading) {
    return (
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded-xl w-40 mb-6"></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {[1, 2].map((i) => (
              <div key={i} className="h-64 bg-gray-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!balance) {
    return (
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full mx-auto mb-6 flex items-center justify-center">
            <FaCoins className="h-10 w-10 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">No Balance Information</h3>
          <p className="text-gray-600 text-lg">Balance information for this user is not available.</p>
        </div>
      </div>
    );
  }

  const currencies: CurrencyCode[] = ['EUR', 'KARMA'];
  const availableCurrencies = currencies;

  if (availableCurrencies.length === 0) {
    return (
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
        <div className="text-center py-12">
          <div className="w-20 h-20 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full mx-auto mb-6 flex items-center justify-center">
            <FaCoins className="h-10 w-10 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-3">No Balance Information</h3>
          <p className="text-gray-600 text-lg">Balance information for this user is not available.</p>
        </div>
      </div>
    );
  }

  const getTotalForCurrency = (currency: CurrencyCode) => {
    const available = balance[currency] || 0;
    const reserved = balance.reserved[currency] || 0;
    const holding = balance.holding[currency] || 0;
    return available + reserved + holding;
  };

  const getBalanceBreakdown = (currency: CurrencyCode) => {
    const available = balance[currency] || 0;
    const reserved = balance.reserved[currency] || 0;
    const holding = balance.holding[currency] || 0;
    const total = available + reserved + holding;
    
    if (total === 0) {
      return {
        available: { value: 0, percentage: 0 },
        reserved: { value: 0, percentage: 0 },
        holding: { value: 0, percentage: 0 }
      };
    }
    
    const availablePercent = (available / total) * 100;
    const reservedPercent = (reserved / total) * 100;
    const holdingPercent = (holding / total) * 100;
    
    return {
      available: { value: available, percentage: Math.round(availablePercent * 10) / 10 },
      reserved: { value: reserved, percentage: Math.round(reservedPercent * 10) / 10 },
      holding: { value: holding, percentage: Math.round(holdingPercent * 10) / 10 }
    };
  };

  return (
    <div className="user-balance-section bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-8 border border-gray-100">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mb-4">
          <FaCoins className="text-white text-2xl" />
        </div>
        <h3 className="text-3xl font-bold text-gray-900 mb-2">Financial Overview</h3>
        <p className="text-gray-600 text-lg">Complete breakdown of user balances and financial status</p>
      </div>

      {/* VAT Information */}
      {balance.vatPercentage !== undefined && (
        <div className="mb-8 text-center">
          <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 text-lg font-semibold rounded-full border border-amber-200 shadow-lg">
            <FaShieldAlt className="mr-3 text-amber-600" />
            VAT Rate: {balance.vatPercentage}%
          </div>
        </div>
      )}
      
      {/* Balance Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {availableCurrencies.map((currency) => {
          const config = currencyConfig[currency];
          const total = getTotalForCurrency(currency);
          const breakdown = getBalanceBreakdown(currency);

          return (
            <div
              key={currency}
              className={`${config.bgColor} rounded-3xl border ${config.borderColor} overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1`}
            >
              {/* Currency Header */}
              <div className={`${config.headerBg} px-6 py-6 border-b ${config.borderColor}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center">
                      <span className="text-3xl">{config.symbol}</span>
                    </div>
                    <div>
                      <h4 className="text-2xl font-bold text-white">{config.name}</h4>
                      <p className="text-white text-opacity-90">Total Balance</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-4xl font-bold text-white">{total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Progress Bar Section */}
              <div className="p-6">
                {/* Enhanced Progress Bar */}
                <div className="mb-6">
                  <div className={`flex h-6 rounded-full overflow-hidden shadow-inner ${isDarkMode ? "bg-gray-700" : "bg-gray-200"}`}>
                    {breakdown.available.percentage > 0 && (
                      <div
                        className={`${config.progressColors.available} transition-all duration-500 ease-out ${isDarkMode ? "shadow-[0_0_20px_rgba(16,185,129,0.35)]" : ""}`}
                        style={{ width: `${breakdown.available.percentage}%` }}
                        title={`Available: ${breakdown.available.value.toFixed(2)} (${breakdown.available.percentage}%)`}
                      />
                    )}
                    {breakdown.reserved.percentage > 0 && (
                      <div
                        className={`${config.progressColors.reserved} transition-all duration-500 ease-out ${isDarkMode ? "shadow-[0_0_20px_rgba(245,158,11,0.35)]" : ""}`}
                        style={{ width: `${breakdown.reserved.percentage}%` }}
                        title={`Reserved: ${breakdown.reserved.value.toFixed(2)} (${breakdown.reserved.percentage}%)`}
                      />
                    )}
                    {breakdown.holding.percentage > 0 && (
                      <div
                        className={`${config.progressColors.holding} transition-all duration-500 ease-out ${isDarkMode ? "shadow-[0_0_20px_rgba(156,163,175,0.25)]" : ""}`}
                        style={{ width: `${breakdown.holding.percentage}%` }}
                        title={`Holding: ${breakdown.holding.value.toFixed(2)} (${breakdown.holding.percentage}%)`}
                      />
                    )}
                  </div>
                </div>

                {/* Balance Breakdown Cards */}
                <div className="space-y-4">
                  {/* Available Balance */}
                  <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-4 border border-green-200 shadow-md hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 bg-green-500 rounded-full shadow-lg"></div>
                        <div>
                          <span className="text-lg font-bold text-green-800">Available</span>
                          <div className="text-sm text-green-600">Ready to use</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-700">{breakdown.available.value.toFixed(2)}</div>
                        <div className="text-sm text-green-600 font-medium">({breakdown.available.percentage}%)</div>
                      </div>
                    </div>
                  </div>

                  {/* Reserved Balance */}
                  <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-2xl p-4 border border-amber-200 shadow-md hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 bg-amber-500 rounded-full shadow-lg"></div>
                        <div>
                          <span className="text-lg font-bold text-amber-800">Reserved</span>
                          <div className="text-sm text-amber-600">Pending transactions</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-amber-700">{breakdown.reserved.value.toFixed(2)}</div>
                        <div className="text-sm text-amber-600 font-medium">({breakdown.reserved.percentage}%)</div>
                      </div>
                    </div>
                  </div>

                  {/* Holding Balance */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4 border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-4 h-4 bg-gray-500 rounded-full shadow-lg"></div>
                        <div>
                          <span className="text-lg font-bold text-gray-800">Holding</span>
                          <div className="text-sm text-gray-600">Temporarily locked</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-gray-700">{breakdown.holding.value.toFixed(2)}</div>
                        <div className="text-sm text-gray-600 font-medium">({breakdown.holding.percentage}%)</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
